import { Phone, Search, Wrench, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Step = {
  number: string;
  duration: string;
  title: string;
  desc: string;
  detail: string[];
  icon: typeof Phone;
};
const STEPS: Step[] = [{
  number: "01",
  duration: "2 minutes",
  title: "Call or message us",
  desc: "Tell us the brand, symptoms, and your suburb. Send a photo or 10-second video over WhatsApp if it helps.",
  detail: ["Free phone diagnosis", "Photo / video review", "Quote within 15 minutes"],
  icon: Phone
}, {
  number: "02",
  duration: "Same day",
  title: "We arrive on-site",
  desc: "Licensed technician arrives in a stocked van. Diagnoses live, confirms the fault, walks you through what's needed.",
  detail: ["Sydney-wide same-day", "Fully insured trades", "ID-verified technician"],
  icon: Search
}, {
  number: "03",
  duration: "45–90 min",
  title: "We fix it on the spot",
  desc: "Parts in the van — we fix on first visit. No return trip, no waiting. Fixed-price quote agreed before any work starts.",
  detail: ["Common parts on board", "Fixed-price guarantee", "Live walkthrough of work"],
  icon: Wrench
}, {
  number: "04",
  duration: "12 months",
  title: "Warranty & follow-up",
  desc: "Test cycle the door 10 times together. Written invoice with parts breakdown. 12-month workmanship warranty in writing.",
  detail: ["Workmanship guarantee", "Parts warranty", "Free re-call if needed"],
  icon: ShieldCheck
}];
export default function RollerProcess() {
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Atmospheric gradients */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 80% 20%, rgba(245,166,35,0.06) 0%, transparent 50%), radial-gradient(circle at 10% 90%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      {/* Grain overlay */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Decorative "PROCESS" watermark */}
      <div aria-hidden style={{
      position: "absolute",
      bottom: -40,
      left: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: "rgba(255,255,255,0.02)",
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 1
    }}>
        PROCESS
      </div>

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1280,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 80,
        alignItems: "end"
      }} className="roller-process-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                04 / How it works
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              From call to{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                fully fixed
              </span>{" "}
              — usually in two hours.
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            No surprise charges, no second trips, no apprentice doing the work.
            One licensed technician, one visit, one fixed price.
          </p>
        </div>

        {/* === TIMELINE === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 0,
        position: "relative"
      }} className="roller-process-grid">
          {/* Connecting gold thread (desktop only) */}
          <div aria-hidden className="roller-process-thread" style={{
          position: "absolute",
          top: 64,
          left: "12.5%",
          right: "12.5%",
          height: 1,
          borderTop: `1px dashed ${GOLD}`,
          opacity: 0.4,
          zIndex: 0
        }} />

          {STEPS.map((step, i) => <StepCard key={i} step={step} isLast={i === STEPS.length - 1} />)}
        </div>

        {/* === BOTTOM TRUST STRIP === */}
        <div style={{
        marginTop: 64,
        padding: "32px 40px",
        background: "rgba(245,166,35,0.06)",
        border: `1px solid ${GOLD}`,
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD,
            marginBottom: 10
          }}>
              Average response · 22 minutes
            </div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(20px, 1.8vw, 24px)",
            fontWeight: 700,
            color: "#ffffff",
            lineHeight: 1.3,
            letterSpacing: "-0.01em"
          }}>
              Most Sydney repairs done same day. Roller door fixed,
              warrantied, done.
            </div>
          </div>

          <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12
        }}>
            <a href="tel:0485000933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 24px",
            background: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontSize: 15,
            fontWeight: 700,
            borderRadius: 8,
            textDecoration: "none",
            whiteSpace: "nowrap",
            transition: "transform 0.2s, box-shadow 0.2s",
            boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
          }} onMouseEnter={e => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
          }} onMouseLeave={e => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
          }}>
              <Phone size={16} strokeWidth={2.5} />
              0485 000 933
            </a>
            <Link to="#contact-us" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "16px 24px",
            background: "transparent",
            color: "#ffffff",
            fontFamily: FONT_HEAD,
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 8,
            textDecoration: "none",
            border: "1px solid rgba(255,255,255,0.3)",
            whiteSpace: "nowrap",
            transition: "background 0.2s, border-color 0.2s"
          }} onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
            (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
          }} onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
            (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.3)";
          }}>
              Get free quote
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 1024px) {
          .roller-process-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .roller-process-thread {
            display: none !important;
          }
        }
        @media (max-width: 600px) {
          .roller-process-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .roller-process-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>;
}

// === Step Card ===
function StepCard({
  step,
  isLast
}: {
  step: Step;
  isLast: boolean;
}) {
  const Icon = step.icon;
  return <div className="roller-step-card" style={{
    position: "relative",
    padding: "0 24px",
    zIndex: 1,
    borderRight: isLast ? "none" : "1px solid rgba(255,255,255,0.06)"
  }}>
      {/* Icon circle */}
      <div style={{
      position: "relative",
      width: 80,
      height: 80,
      marginBottom: 28,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
        {/* Outer ring */}
        <div style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        border: `1px solid ${GOLD}`,
        opacity: 0.3
      }} />
        {/* Inner solid */}
        <div style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        background: NAVY_DEEP,
        border: `1px solid rgba(245,166,35,0.3)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)"
      }}>
          <Icon size={26} color={GOLD} strokeWidth={1.8} />
        </div>
      </div>

      {/* Number + duration */}
      <div style={{
      display: "flex",
      alignItems: "baseline",
      gap: 12,
      marginBottom: 16
    }}>
        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: 32,
        fontWeight: 800,
        color: GOLD,
        letterSpacing: "-0.02em",
        lineHeight: 1
      }}>
          {step.number}
        </span>
        <div style={{
        height: 1,
        width: 20,
        background: "rgba(245,166,35,0.4)"
      }} />
        <span style={{
        fontFamily: FONT_BODY,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.15em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.5)"
      }}>
          {step.duration}
        </span>
      </div>

      {/* Title */}
      <h3 style={{
      fontFamily: FONT_HEAD,
      fontSize: 20,
      fontWeight: 700,
      color: "#ffffff",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
      margin: "0 0 12px"
    }}>
        {step.title}
      </h3>

      {/* Description */}
      <p style={{
      fontFamily: FONT_BODY,
      fontSize: 14,
      color: "rgba(255,255,255,0.65)",
      lineHeight: 1.55,
      margin: "0 0 20px"
    }}>
        {step.desc}
      </p>

      {/* Detail bullets */}
      <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 6
    }}>
        {step.detail.map((d, i) => <li key={i} style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontFamily: FONT_BODY,
        fontSize: 12,
        color: "rgba(255,255,255,0.55)",
        letterSpacing: "0.01em"
      }}>
            <span style={{
          width: 3,
          height: 3,
          borderRadius: "50%",
          background: GOLD,
          flexShrink: 0
        }} />
            {d}
          </li>)}
      </ul>
    </div>;
}